import { NavLink } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Small } from "@/components/ui/typography";
import type { NavGroup } from "@/lib/navigation";
import type { NavUser } from "@/lib/userDisplay";
import { cn } from "@/lib/utils";
import { CaretRightIcon, SignOutIcon } from "@phosphor-icons/react";
import { AuthButtons } from "./AuthButtons";
import { NavBadge } from "./NavBadge";
import { UserCard } from "./UserIdentity";
import { ThemeSegmented } from "@/components/theme-toggle";

interface NavMenuProps {
  groups: NavGroup[];
  user: NavUser | null;
  onSignOut: () => void;
  /** Linkre kattintva – a hívó ezzel zárja a panelt vagy a dialógust */
  onNavigate: () => void;
  /**
   * A `nav` landmark felolvasott neve. Kötelező: a menü két helyen is a lapon lehet
   * (fejléc panel + fiók dialógus), és két azonos nevű navigációs landmark között a
   * képernyőolvasó használója nem tudna választani.
   */
  label: string;
}

/**
 * A navigációs menü tartalma: csoportosított linkek, alul fiók/kijelentkezés vagy belépés.
 * Két helyen jelenik meg, ugyanabból a kódból: a fejléc mobil lenyíló paneljében és az
 * asztali avatar `UserMenu` dialógusában - a sorok kinézete így egy helyen él.
 */
export function NavMenu({ groups, user, onSignOut, onNavigate, label }: NavMenuProps) {
  return (
    // A linkek és a fiók-blokk közti térközt a menü adja, nem a befogadója: a fejléc
    // paneljében egy flex `gap` állta, a `UserMenu` dialógusában viszont semmi - ott az
    // elválasztó vonal a legalsó link aljához ért, alatta levegővel, fölötte nélküle.
    <div className="flex flex-col gap-4">
      <nav aria-label={label} className="space-y-1">
        {groups.map((group, groupIndex) => (
          <div key={group.title ?? groupIndex}>
            {group.title && (
              <Small render={<p />} className="px-3 pt-2 pb-1 text-muted-foreground">
                {group.title}
              </Small>
            )}
            {group.items.map((item) => {
              const Icon = item.icon;
              return (
                <NavLink
                  key={item.path}
                  to={item.path}
                  end={item.end}
                  onClick={onNavigate}
                  className={({ isActive }) =>
                    cn(
                      "flex w-full items-center gap-3 rounded-lg px-3 py-3 text-sm font-medium transition-colors",
                      isActive ? "bg-primary text-primary-foreground" : "text-foreground hover:bg-muted",
                    )
                  }
                >
                  {({ isActive }) => (
                    <>
                      <Icon className="size-5 shrink-0" aria-hidden="true" />
                      <span>{item.label}</span>
                      {item.badge ? <NavBadge count={item.badge} active={isActive} /> : null}
                      <CaretRightIcon
                        className={cn(
                          "ml-auto size-4",
                          isActive ? "text-primary-foreground/70" : "text-muted-foreground/70",
                        )}
                        aria-hidden="true"
                      />
                    </>
                  )}
                </NavLink>
              );
            })}
          </div>
        ))}
      </nav>

      {/* Megjelenés választó */}
      <div className="flex flex-col gap-2 border-t pt-3">
        <Small render={<span />} className="px-1 text-xs font-medium text-muted-foreground">
          Megjelenés
        </Small>
        <ThemeSegmented />
      </div>

      <div className="flex flex-col gap-3 border-t pt-4">
        {user ? (
          <>
            <UserCard user={user} className="rounded-lg border bg-background p-3" />
            <Button
              variant="destructive"
              className="w-full"
              onClick={() => {
                onNavigate();
                onSignOut();
              }}
            >
              <SignOutIcon data-icon="inline-start" aria-hidden="true" />
              Kijelentkezés
            </Button>
          </>
        ) : (
          <AuthButtons layout="stack" onNavigate={onNavigate} />
        )}
      </div>
    </div>
  );
}
